import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useT } from "../i18n";
import { wingChunTopics } from "../data/wingchunTopics";

const WingChun = () => {
  const { lang } = useLanguage();
  const t = useT(lang);

  const principles_el = [
    { h: "Κεντρική γραμμή", p: "Ο ασκούμενος μαθαίνει να προστατεύει το κέντρο του σώματός του και ταυτόχρονα να επιτίθεται στη γραμμή του αντιπάλου με άμεσο και ελεγχόμενο τρόπο." },
    { h: "Οικονομία της κίνησης", p: "Κάθε τεχνική έχει σκοπό, κατεύθυνση και πρακτική αξία. Η απλότητα δεν σημαίνει φτώχεια τεχνικής, αλλά καθαρότητα και αποτελεσματικότητα." },
    { h: "Χαλάρωση και δομή", p: "Η δύναμη προέρχεται από τη σωστή στάση, τη χαλάρωση, τη σύνδεση των αρθρώσεων και τη σωστή μεταφορά της ενέργειας — όχι μόνο από τους μυς." },
    { h: "Άμεση αντίδραση", p: "Μέσα από ασκήσεις όπως το Chi Sao, ο μαθητής αναπτύσσει αισθητηριακή αντίληψη — την ικανότητα να νιώθει την πίεση και την πρόθεση του αντιπάλου μέσω της επαφής." },
  ];

  const principles_en = [
    { h: "Centerline", p: "The student learns to protect the center of their body and simultaneously attack along the opponent's line, directly and with control." },
    { h: "Economy of motion", p: "Every technique has purpose, direction and practical value. Simplicity is not poverty of technique — it is clarity and effectiveness." },
    { h: "Relaxation and structure", p: "Power comes from correct posture, relaxation, joint connection and proper energy transfer — not just from muscles." },
    { h: "Immediate reaction", p: "Through exercises like Chi Sao, the student develops sensory awareness — the ability to feel the opponent's pressure and intention through contact." },
  ];

  const principles = lang === "el" ? principles_el : principles_en;

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden py-24 md:py-32">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1555597673-b21d5c935865?w=1920&q=80"
            alt=""
            className="w-full h-full object-cover img-moody"
          />
          <div className="absolute inset-0 bg-[#0A0A0A]/75" />
        </div>
        <div className="absolute right-[-8%] top-[-4%] cn-bg">詠春</div>

        <div className="container-wc relative z-10 max-w-4xl fade-up">
          <div className="flex items-center gap-3 mb-4">
            <span className="divider-gold" />
            <span className="eyebrow">Wing Chun</span>
          </div>
          <h1 className="h-display text-5xl md:text-7xl mb-6">
            {lang === "el" ? "Wing Chun" : "Wing Chun"}
          </h1>
          <p className="text-gold text-xl font-serif italic mb-8">
            {lang === "el"
              ? "Απλότητα – Ταχύτητα – Αποτελεσματικότητα"
              : "Simplicity – Speed – Effectiveness"}
          </p>
          <p className="text-dim text-lg leading-relaxed max-w-3xl">
            {lang === "el"
              ? "Μια κινέζικη πολεμική τέχνη και ένα ολοκληρωμένο σύστημα αυτοάμυνας, βασισμένο στην οικονομία της κίνησης, την κεντρική γραμμή και την άμεση αντίδραση."
              : "A Chinese martial art and complete self-defense system, based on economy of motion, the centerline and immediate response."}
          </p>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-20 md:py-28">
        <div className="container-wc grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8 space-y-6 text-[#E8E8E0] text-lg leading-relaxed">
            <p>
              {lang === "el"
                ? "Το Wing Chun δεν στηρίζεται στη σωματική δύναμη, αλλά στη σωστή τεχνική, στην αντίληψη, στην ισορροπία και στον έλεγχο της απόστασης. Σε αντίθεση με την αντίληψη ότι μια πολεμική τέχνη είναι μια σειρά από έτοιμες απαντήσεις, το Wing Chun διδάσκει αρχές."
                : "Wing Chun does not rely on physical strength but on correct technique, awareness, balance and control of distance. Unlike the notion that a martial art is a series of ready-made responses, Wing Chun teaches principles."}
            </p>
            <p>
              {lang === "el"
                ? "Ο ασκούμενος μαθαίνει να κατανοεί την κίνηση, να προσαρμόζεται και να αντιδρά με ψυχραιμία, ταχύτητα και ακρίβεια. Οι τεχνικές του είναι σύντομες, άμεσες και πρακτικές — χωρίς περιττές κινήσεις ή εντυπωσιασμούς."
                : "The practitioner learns to understand movement, adapt and react with composure, speed and precision. Its techniques are short, direct and practical — no superfluous motion or showiness."}
            </p>
          </div>
          <div className="lg:col-span-4">
            <div className="card bg-primary/10 border-primary/30">
              <span className="font-cn text-gold text-4xl block mb-3">拳</span>
              <h3 className="font-serif text-xl mb-2">
                {lang === "el" ? "Ιδανικό για" : "Suitable for"}
              </h3>
              <ul className="text-dim text-sm space-y-2 leading-relaxed">
                <li>— {lang === "el" ? "Αρχάριους & προχωρημένους" : "Beginners & advanced"}</li>
                <li>— {lang === "el" ? "Γυναίκες" : "Women"}</li>
                <li>— {lang === "el" ? "Εφήβους & ενήλικες" : "Teenagers & adults"}</li>
                <li>— {lang === "el" ? "Ασκούμενους άλλων τεχνών" : "Practitioners of other arts"}</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="py-20 md:py-28 bg-surface relative overflow-hidden">
        <div className="absolute left-[-15%] top-[-30%] cn-bg" style={{ writingMode: "horizontal-tb" }}>道</div>
        <div className="container-wc relative">
          <div className="flex items-center gap-3 mb-4">
            <span className="divider-gold" />
            <span className="eyebrow">{lang === "el" ? "Βασικές αρχές" : "Core principles"}</span>
          </div>
          <h2 className="h-display text-4xl md:text-5xl mb-12 max-w-2xl">
            {lang === "el" ? "Οι βασικές αρχές του Wing Chun" : "The core principles of Wing Chun"}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {principles.map((pr, i) => (
              <div key={pr.h} className="card">
                <span className="eyebrow">0{i + 1}</span>
                <h3 className="font-serif text-2xl mt-2 mb-3">{pr.h}</h3>
                <p className="text-dim leading-relaxed text-sm">{pr.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TOPICS INDEX */}
      <section className="py-20 md:py-28">
        <div className="container-wc">
          <div className="flex items-center gap-3 mb-4">
            <span className="divider-gold" />
            <span className="eyebrow">{lang === "el" ? "Εξερευνήστε" : "Explore"}</span>
          </div>
          <h2 className="h-display text-4xl md:text-5xl mb-12 max-w-2xl">
            {lang === "el" ? "Βαθύτερη γνώση" : "Deeper knowledge"}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {wingChunTopics.map((topic) => (
              <Link
                key={topic.slug}
                to={`/wing-chun/${topic.slug}`}
                data-testid={`topic-${topic.slug}`}
                className="card group block"
              >
                <div className="aspect-[4/3] -mx-8 -mt-8 mb-6 overflow-hidden border-b border-goldSoft">
                  <img
                    src={topic.cover}
                    alt=""
                    className="w-full h-full object-cover img-moody group-hover:scale-105"
                    style={{ transition: "transform 600ms ease" }}
                  />
                </div>
                <h3 className="font-serif text-2xl mb-2 group-hover:text-gold transition-colors">
                  {lang === "el" ? topic.label_el : topic.label_en}
                </h3>
                <p className="text-dim text-sm line-clamp-2">
                  {lang === "el" ? topic.intro_el : topic.intro_en}
                </p>
                <span className="inline-flex items-center gap-2 text-gold text-xs uppercase tracking-widest mt-4">
                  {lang === "el" ? "Διαβάστε" : "Read"} <ArrowRight size={12} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 border-t border-goldSoft">
        <div className="container-wc text-center max-w-3xl mx-auto">
          <span className="font-cn text-gold text-5xl block mb-4">學</span>
          <h2 className="h-display text-3xl md:text-4xl mb-6">
            {lang === "el" ? "Μάθετε Wing Chun με προσωπική καθοδήγηση" : "Learn Wing Chun with personal guidance"}
          </h2>
          <Link to="/contact" data-testid="wingchun-cta" className="btn-primary">
            {t.hero.cta_primary} <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default WingChun;
